import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { StatCard } from "../components/shared/StatCard";
import { Modal } from "../components/shared/Modal";
import { Spinner } from "../components/shared/Spinner";
import { tablesAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { fmt } from "../components/shared/fmt";
import { useTitle } from "../hooks/useTitle";

export const TablesPage: React.FC = () => {
	useTitle("Tables");
	const { user } = useAuth();
	const qc = useQueryClient();
	const { data, isLoading } = useQuery({
		queryKey: ["tables"],
		queryFn: () => tablesAPI.getAll(),
	});
	const tables = data?.data || [];
	const isCashier = user?.role === "cashier";
	const [fSec, setFSec] = useState("All");
	const [modal, setModal] = useState(false);
	const [sel, setSel] = useState<any>(null);
	const [form, setForm] = useState({
		label: "",
		section: "Main Hall",
		capacity: "4",
	});

	const saveMut = useMutation({
		mutationFn: (d: any) =>
			sel ? tablesAPI.update(sel.id, d) : tablesAPI.create(d),
		onSuccess: () => {
			toast.success(sel ? "Updated" : "Added");
			qc.invalidateQueries({ queryKey: ["tables"] });
			setModal(false);
		},
	});
	const statusMut = useMutation({
		mutationFn: ({ id, status }: { id: string; status: string }) =>
			tablesAPI.setStatus(id, { status }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["tables"] }),
	});
	const delMut = useMutation({
		mutationFn: tablesAPI.delete,
		onSuccess: () => {
			toast.success("Deleted");
			qc.invalidateQueries({ queryKey: ["tables"] });
		},
	});

	const sections = [
		"All",
		...Array.from(new Set(tables.map((t: any) => t.section))),
	];
	const visible = tables.filter(
		(t: any) => fSec === "All" || t.section === fSec,
	);

	if (isLoading) return <Spinner />;

	return (
		<div>
			<div className="page-title">
				{isCashier ? "Floor Plan" : "Table Management"}
			</div>
			<div className="page-sub">
				{tables.length} tables ·{" "}
				{tables.reduce((s: number, t: any) => s + t.capacity, 0)} capacity
			</div>

			<div className="stat-grid">
				<StatCard
					label="Available"
					value={tables.filter((t: any) => t.status === "available").length}
					cls="green"
					icon="✅"
				/>
				<StatCard
					label="Occupied"
					value={tables.filter((t: any) => t.status === "occupied").length}
					cls="red"
					icon="🔴"
				/>
				<StatCard
					label="Reserved"
					value={tables.filter((t: any) => t.status === "reserved").length}
					cls=""
					icon="📅"
				/>
				<StatCard
					label="Revenue"
					value={fmt(
						tables
							.filter((t: any) => t.status === "occupied")
							.reduce((s: number, t: any) => s + (t.revenue || 0), 0),
					)}
					cls="green"
					icon="💰"
				/>
			</div>

			<div className="card">
				<div className="card-title">
					Floor Plan
					<div className="card-actions">
						<div className="filter-tabs" style={{ marginBottom: 0 }}>
							{sections.map((s) => (
								<button
									key={String(s)}
									className={`filter-tab ${fSec === s ? "active" : ""}`}
									onClick={() => setFSec(String(s))}>
									{String(s)}
								</button>
							))}
						</div>
						{!isCashier && (
							<button
								className="btn-primary"
								onClick={() => {
									setSel(null);
									setForm({ label: "", section: "Main Hall", capacity: "4" });
									setModal(true);
								}}>
								+ Add Table
							</button>
						)}
					</div>
				</div>

				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))",
						gap: 12,
						marginTop: 14,
					}}>
					{visible.map((t: any) => {
						const isRes = t.status === "reserved";
						const bColor =
							t.status === "occupied"
								? "rgba(239,68,68,.5)"
								: isRes
									? "rgba(168,85,247,.5)"
									: "var(--border)";
						const bgColor =
							t.status === "occupied"
								? "rgba(239,68,68,.04)"
								: isRes
									? "rgba(168,85,247,.04)"
									: "var(--card2)";
						return (
							<div
								key={t.id}
								style={{
									background: bgColor,
									borderRadius: "var(--r)",
									border: `2px solid ${bColor}`,
									padding: 16,
									textAlign: "center",
									position: "relative",
								}}>
								<div
									style={{
										position: "absolute",
										top: 8,
										right: 8,
										fontSize: 10,
										color: "var(--muted)",
										fontWeight: 600,
									}}>
									👥{t.capacity}
								</div>
								<div
									style={{
										fontFamily: "Syne,sans-serif",
										fontSize: 22,
										fontWeight: 800,
										marginBottom: 4,
									}}>
									{t.label}
								</div>
								<div
									style={{
										fontSize: 10,
										color: "var(--muted)",
										fontWeight: 600,
										textTransform: "uppercase",
										letterSpacing: ".4px",
										marginBottom: 6,
									}}>
									{t.section}
								</div>
								<span className={`badge badge-${t.status}`}>{t.status}</span>
								{!isCashier && (
									<>
										<select
											className="form-select"
											style={{
												fontSize: 10,
												padding: "4px 6px",
												width: "100%",
												marginTop: 10,
											}}
											value={t.status}
											onChange={(e) =>
												statusMut.mutate({ id: t.id, status: e.target.value })
											}>
											{["available", "occupied", "reserved"].map((s) => (
												<option key={s}>{s}</option>
											))}
										</select>
										<div style={{ display: "flex", gap: 4, marginTop: 8 }}>
											<button
												className="btn-secondary"
												style={{ flex: 1, fontSize: 10 }}
												onClick={() => {
													setSel(t);
													setForm({
														label: t.label,
														section: t.section,
														capacity: String(t.capacity),
													});
													setModal(true);
												}}>
												✏️
											</button>
											<button
												className="btn-secondary btn-danger"
												style={{ fontSize: 10 }}
												onClick={() => {
													if (window.confirm("Delete table?"))
														delMut.mutate(t.id);
												}}>
												🗑
											</button>
										</div>
									</>
								)}
								{isCashier && (
									<button
										className="btn-primary"
										style={{
											width: "100%",
											marginTop: 10,
											fontSize: 11,
											padding: "7px 6px",
											opacity: isRes ? 0.55 : 1,
										}}
										onClick={() =>
											!isRes && toast.success(`Opening POS for ${t.label}…`)
										}>
										{isRes
											? "🔒 Reserved"
											: t.status === "occupied"
												? "🧾 Add to Order"
												: "➕ New Order"}
									</button>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{modal && (
				<Modal
					title={sel ? "Edit Table" : "Add Table"}
					onClose={() => setModal(false)}
					onConfirm={() =>
						saveMut.mutate({
							label: form.label,
							section: form.section,
							capacity: parseInt(form.capacity) || 4,
						})
					}
					confirmText={sel ? "Save" : "Add"}>
					<div className="form-row cols-3">
						<div className="form-group">
							<label className="form-label">Label</label>
							<input
								className="form-input"
								value={form.label}
								onChange={(e) =>
									setForm((f) => ({ ...f, label: e.target.value }))
								}
								placeholder="T15"
							/>
						</div>
						<div className="form-group">
							<label className="form-label">Section</label>
							<select
								className="form-select"
								value={form.section}
								onChange={(e) =>
									setForm((f) => ({ ...f, section: e.target.value }))
								}>
								{["Main Hall", "Terrace", "Private", "Bar", "Outdoor"].map(
									(s) => (
										<option key={s}>{s}</option>
									),
								)}
							</select>
						</div>
						<div className="form-group">
							<label className="form-label">Capacity</label>
							<input
								className="form-input"
								type="number"
								min={1}
								max={20}
								value={form.capacity}
								onChange={(e) =>
									setForm((f) => ({ ...f, capacity: e.target.value }))
								}
							/>
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};
